import React, { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { useNavigate } from "react-router-dom";
import { Star, Sparkles, Trophy, Zap, Lock } from "lucide-react";
import { useGuest } from "./GuestSystem";
import { useApp } from "../context";

/**
 * GuestStarMilestones, celebrates guest "wow" moments as their star count
 * ticks past key milestones during their 5-minute preview.
 *
 * Milestones:
 *   1 ⭐  → small burst + "First star!"
 *   3 ⭐  → medium burst + "You're on fire!"
 *   5 ⭐  → bigger burst + "Star explorer!"
 *   10 ⭐ → finale burst + "Unstoppable!"
 *
 * Guards:
 *   - Fires only for guest (isDemo) users
 *   - Each milestone fires at most once per session
 *   - Respects prefers-reduced-motion (skips confetti, keeps the toast)
 *   - Uses a soft Web Audio ping so no asset files are required
 */

const MILESTONES = [
  { stars: 1, title: "First star!", subtitle: "You earned your very first star ⭐", icon: Star, color: ["#FCD34D", "#F59E0B"], burst: "small" },
  { stars: 3, title: "You're on fire!", subtitle: "3 stars already, keep it up 🔥", icon: Zap, color: ["#FB923C", "#EF4444"], burst: "medium" },
  { stars: 5, title: "Star explorer!", subtitle: "5 stars · you're a natural 🌟", icon: Sparkles, color: ["#A78BFA", "#EC4899"], burst: "large" },
  { stars: 10, title: "Unstoppable!", subtitle: "10 stars · save them with a free account 🏆", icon: Trophy, color: ["#10B981", "#06B6D4", "#F59E0B"], burst: "finale" },
];

const playChime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    // Two quick notes, C5 → E5 for a happy ping
    [523.25, 659.25].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + i * 0.08);
      gain.gain.linearRampToValueAtTime(0.12, now + i * 0.08 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.08 + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.08);
      osc.stop(now + i * 0.08 + 0.4);
    });
    setTimeout(() => ctx.close(), 900);
  } catch (_) {}
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const fireBurst = (size, colors) => {
  if (prefersReducedMotion()) return;
  const defaults = { colors, zIndex: 9998, disableForReducedMotion: true };
  if (size === "small") {
    confetti({ ...defaults, particleCount: 40, spread: 55, origin: { x: 0.5, y: 0.75 }, startVelocity: 32 });
  } else if (size === "medium") {
    confetti({ ...defaults, particleCount: 90, spread: 75, origin: { x: 0.5, y: 0.7 }, startVelocity: 40 });
  } else if (size === "large") {
    confetti({ ...defaults, particleCount: 140, spread: 100, origin: { x: 0.5, y: 0.65 }, startVelocity: 50, scalar: 1.1 });
    setTimeout(() => confetti({ ...defaults, particleCount: 60, spread: 60, origin: { x: 0.2, y: 0.7 } }), 180);
    setTimeout(() => confetti({ ...defaults, particleCount: 60, spread: 60, origin: { x: 0.8, y: 0.7 } }), 320);
  } else if (size === "finale") {
    const end = Date.now() + 1200;
    const tick = () => {
      confetti({ ...defaults, particleCount: 6, angle: 60, spread: 55, origin: { x: 0, y: 0.8 } });
      confetti({ ...defaults, particleCount: 6, angle: 120, spread: 55, origin: { x: 1, y: 0.8 } });
      if (Date.now() < end) requestAnimationFrame(tick);
    };
    tick();
  }
};

const STORAGE_KEY = "isaiah_guest_milestones_fired";

const GuestStarMilestones = () => {
  const { user } = useApp();
  const { isGuest, progress } = useGuest();
  const navigate = useNavigate();
  const firedRef = useRef(new Set());
  const [toast, setToast] = useState(null); // { title, subtitle, Icon, gradient }

  // Hydrate fired set from sessionStorage so a refresh doesn't replay
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) firedRef.current = new Set(JSON.parse(raw));
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!user?.isDemo || !isGuest) return;
    const stars = progress?.stars ?? 0;
    for (const m of MILESTONES) {
      if (stars >= m.stars && !firedRef.current.has(m.stars)) {
        firedRef.current.add(m.stars);
        try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(firedRef.current))); } catch (_) {}
        fireBurst(m.burst, m.color);
        playChime();
        setToast({
          title: m.title,
          subtitle: m.subtitle,
          Icon: m.icon,
          gradient: `linear-gradient(135deg, ${m.color.join(", ")})`,
        });
        setTimeout(() => setToast(null), 3200);
        break; // Only fire one per update cycle even if multiple milestones are crossed
      }
    }
  }, [user, isGuest, progress?.stars]);

  if (!toast) return null;

  const { title, subtitle, Icon, gradient } = toast;

  return (
    <div
      className="below-header-fixed left-1/2 -translate-x-1/2 z-[9999] animate-in fade-in slide-in-from-top-4 duration-500"
      data-testid="guest-star-milestone-toast"
    >
      <div
        className="rounded-2xl px-5 py-3 shadow-2xl flex items-center gap-3 min-w-[300px] max-w-md pointer-events-auto"
        style={{ background: gradient, boxShadow: "0 10px 40px -10px rgba(0,0,0,0.4)" }}
      >
        <div className="w-10 h-10 rounded-full bg-white/25 border border-white/35 flex items-center justify-center shrink-0">
          <Icon size={20} className="text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-white font-fredoka font-bold text-base leading-tight">{title}</p>
          <p className="text-white/85 text-xs leading-snug">{subtitle}</p>
        </div>
        {/* Save your stars → converts guest's peak-emotion moment into a signup */}
        <button
          onClick={() => {
            setToast(null);
            navigate("/signup");
          }}
          className="shrink-0 px-3 py-1.5 rounded-lg bg-white/25 hover:bg-white/35 border border-white/40 text-white text-[11px] font-fredoka font-bold flex items-center gap-1 transition-all"
          data-testid="guest-star-save-btn"
          aria-label="Save your stars by signing up"
        >
          <Lock size={11} /> Save stars
        </button>
      </div>
    </div>
  );
};

export default GuestStarMilestones;
